import { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { companiesDnbActions } from '@redux/reducers/common/companies-dnb/companiesDnbReducer';
import { companiesDnbSelectors } from '@redux/selectors/common/commonCompaniesDnb';
import { Autocomplete } from '@shared/components/autocomplete';
import { Button } from '@shared/components/button-new';
import { Flex } from '@shared/components/flex';

import { styles } from './LookupCompany.styles';

export enum LookupCompanyModes {
  DnB = 'dnb',
}

export interface LookupCompanyProps extends WithStyles<typeof styles> {
  search: string;
  mode: LookupCompanyModes;
  renderCompanyOption: (company: any) => JSX.Element | string;
  onCreateNewCompany: (name: string) => void;
  onManuallyCreate: () => void;
  onSearchChange: (search: string) => void;
  onSelectCompany: (company: any) => void;
}

interface LookupActions {
  getList: (search: string) => void;
  clearList: () => void;
}

const LookupCompanyComponent: FC<LookupCompanyProps> = ({
  classes,
  search,
  mode,
  renderCompanyOption,
  onCreateNewCompany,
  onManuallyCreate,
  onSearchChange,
  onSelectCompany,
}) => {
  const dispatch = useDispatch();
  const state = useSelector(companiesDnbSelectors.getListState);

  const actions = useMemo((): LookupActions => {
    switch (mode) {
      case LookupCompanyModes.DnB:
        return {
          getList: (search: string) => {
            dispatch(companiesDnbActions.getListRequest({ search }));
          },
          clearList: () => dispatch(companiesDnbActions.clearList()),
        };
    }
  }, [dispatch, mode]);

  useEffect(() => {
    if (search) {
      actions.getList(search);
    }

    return () => {
      actions.clearList();
    };
  }, []);

  const handleSelectCompany = (company: any) => {
    onSelectCompany(company);

    actions.clearList();
  };

  const handeChangeSearch = (search: string) => {
    onSearchChange(search);
  };

  return (
    <Flex direction="column" alignItems="flex-start" className={classes.root}>
      <span className={classes.heading}>{t('lookup_a_company')}</span>
      <Autocomplete
        actionRow={
          <Flex wrap="nowrap" alignItems="center" justifyContent="space-between">
            <span>{`${t('create_company_called')} "${search}"`}</span>
            <Button
              text={t('create_new_company')}
              onClick={() => onCreateNewCompany(search)}
              classes={{ root: classes.createCompanyBtn }}
            />
          </Flex>
        }
        classes={{
          root: classes.autocomplete,
          actionRow: classes.autocompleteActionRow,
          optionsList: classes.autocompleteOptions,
          notFoundText: classes.autocompleteNotFound,
        }}
        error={!!state.error}
        inputProps={{
          autoFocus: true,
          errorText: state.error || undefined,
          placeholder: t('xtextx_ellipsis', { text: t('search_by_company_name') }),
        }}
        list={state.list}
        loading={state.loading}
        searchValue={search}
        withClearIcon
        onClearList={() => dispatch(companiesDnbActions.clearList())}
        onGetList={(search) => dispatch(companiesDnbActions.getListRequest({ search }))}
        onSearchValueChange={handeChangeSearch}
        onSelect={handleSelectCompany}
        renderOption={renderCompanyOption}
      />
      <Flex direction="column" autoWidth={false}>
        <span className={classes.manuallyCreateText}>
          {t('if_you_cant_find_the_company_above_manually_create_a_new_company_colon')}
        </span>
        <Button
          classes={{ root: classes.manuallyCreateBtn }}
          text={t('manually_create_company')}
          onClick={onManuallyCreate}
        />
      </Flex>
    </Flex>
  );
};

export const LookupCompany = withStyles(styles)(LookupCompanyComponent);
